import type { RouteObject } from 'react-router-dom';
import { lazy } from 'react';
import ProtectedRoute from '@/middleware/ProtectedRoute';
import LanguageGuard from '@/middleware/LanguageGuard';
import { USER_ROLES } from '@/utils/common';
import MainLayout from '@/layouts/MainLayout';
import AdminLayout from '@/layouts/AdminLayout';
const Home = lazy(() => import('@/pages/website/home/page'));
const About = lazy(() => import('@/pages/website/about/page'));

const News = lazy(() => import('@/pages/website/news/page'));
const NewsDetail = lazy(() => import('@/pages/website/news/detail/page'));
const Contact = lazy(() => import('@/pages/website/contact/page'));

const HotelDetail = lazy(() => import('@/pages/website/hotels/detail/page'));

const Rooms = lazy(() => import('@/pages/website/rooms/page'));
const RoomDetail = lazy(() => import('@/pages/website/rooms/detail/page'));

const SearchResults = lazy(() => import('@/pages/website/search/page'));

const Login = lazy(() => import('@/pages/auth/login/page'));
const Register = lazy(() => import('@/pages/auth/register/page'));

const Privacy = lazy(() => import('@/pages/website/privacy/page'));
const Terms = lazy(() => import('@/pages/website/terms/page'));

const AdminDashboard = lazy(() => import('@/pages/admin/dashboard/page'));
const AdminHomeManagement = lazy(
  () => import('@/pages/admin/home-management/page')
);
const PermissionManagement = lazy(() => import('@/pages/admin/permission/page'))

const AdminNewsCategories = lazy(
  () => import('@/pages/admin/news-categories/page')
);
const AdminNews = lazy(() => import('@/pages/admin/news/page'));
const AdminNewsForm = lazy(() => import('@/pages/admin/news/form/page'));
const AdminNewsEdit = lazy(() => import('@/pages/admin/news/edit/page'));
const AdminContacts = lazy(() => import('@/pages/admin/contacts/page'));
const AdminProfile = lazy(() => import('@/pages/admin/profile/page'));

const AdminHotel = lazy(() => import('@/pages/admin/hotels/page'));
const AdminHotelForm = lazy(() => import('@/pages/admin/hotels/form/page'));
const AdminHotelEdit = lazy(() => import('@/pages/admin/hotels/edit/page'));

const AdminHotelRoomType = lazy(
  () => import('@/pages/admin/hotels/room-type/page')
);

const AdminRoom = lazy(() => import('@/pages/admin/rooms/page'));
const AdminRoomForm = lazy(() => import('@/pages/admin/rooms/form/page'));
const AdminRoomEdit = lazy(() => import('@/pages/admin/rooms/edit/page'));

const AdminTextBlocks = lazy(() => import('@/pages/admin/text-blocks/page'));

const Page404 = lazy(() => import('@/pages/error/Page404'));

const LanguageSelect = lazy(
  () => import('@/pages/website/onboarding/LanguageSelect')
);

const AdminContactInfo = lazy(() => import('@/pages/admin/ContactInfo/page'))

const routes: RouteObject[] = [
  {
    path: '/language', element: <LanguageSelect/>
  },
  {
    path: '/',
    element: (
      <LanguageGuard>
        <MainLayout />
      </LanguageGuard>
    ),
    children: [
      { index: true, element: <Home /> },
      { path: 'about', element: <About /> },
      { path: 'news', element: <News /> },
      { path: 'news/:id', element: <NewsDetail /> },
      { path: 'contact', element: <Contact /> },
      { path: 'search-results', element: <SearchResults /> },
      { path: 'hotels/:id', element: <HotelDetail /> },
      { path: 'rooms', element: <Rooms /> },
      { path: 'rooms/:id', element: <RoomDetail /> },
      { path: 'privacy', element: <Privacy /> },
      { path: 'terms', element: <Terms /> },
    ],
  },
  {
    path: 'admin',
    element: (
      <ProtectedRoute
        element={<AdminLayout />}
        allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.USER]}
      />
    ),
    children: [
      { index: true, path: '/admin', element: <AdminDashboard /> },
      { path: '/admin/dashboard', element: <AdminDashboard /> },
      { path: '/admin/home-management', element: <AdminHomeManagement /> },

      { path: '/admin/news-categories', element: <AdminNewsCategories /> },
      { path: '/admin/news', element: <AdminNews /> },
      { path: '/admin/news/add', element: <AdminNewsForm /> },
      { path: '/admin/news/edit/:id', element: <AdminNewsEdit /> },
      { path: '/admin/contacts', element: <AdminContacts /> },

      { path: '/admin/hotels', element: <AdminHotel /> },
      { path: '/admin/hotels/add', element: <AdminHotelForm /> },

      { path: '/admin/hotels/edit/:id', element: <AdminHotelEdit /> },
      { path: '/admin/hotels/room-type', element: <AdminHotelRoomType /> },

      { path: '/admin/hotels/rooms', element: <AdminRoom /> },

      { path: '/admin/hotels/rooms/add', element: <AdminRoomForm /> },
      { path: '/admin/hotels/rooms/edit/:id', element: <AdminRoomEdit /> },
      {
        path: '/admin/home-management/text-blocks',
        element: <AdminTextBlocks />,
      },
      { path: '/admin/profile', element: <AdminProfile /> },
      { path: '/admin/contact-info', element: <AdminContactInfo/> },
      { path: '/admin/permission', element: <PermissionManagement/> }
    ],
  },
  { path: '/login', element: <Login /> },
  { path: '/register', element: <Register /> },
  { path: '*', element: <Page404 /> },
];

export default routes;
